/**
 * Hopefield motion helpers for hf-* sections.
 * - Reveals: elements with [data-hf-reveal] fade up once when they enter the viewport, staggered within their
 *   section. The CSS (sections/hf-site-style.liquid) only hides them while this script is running, so content
 *   stays visible without it and in the theme editor.
 * - Drag rows: [data-hf-drag] scrollers get mouse dragging and arrow-key paging (touch and trackpads scroll
 *   natively).
 */
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
      )
    : null;

/** @param {ParentNode} root */
function scanReveals(root) {
  const targets = [...root.querySelectorAll('[data-hf-reveal]:not(.is-in)')];
  const counts = new Map();
  for (const el of targets) {
    if (!(el instanceof HTMLElement)) continue;
    const group = el.closest('section, article, footer') || document.body;
    const n = counts.get(group) || 0;
    el.style.setProperty('--hf-d', `${Math.min(n, 6) * 75}ms`);
    counts.set(group, n + 1);
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-in');
  }
}

/** @param {ParentNode} root */
function setupDragRows(root) {
  for (const row of root.querySelectorAll('[data-hf-drag]')) {
    if (!(row instanceof HTMLElement) || row.dataset.hfDragReady) continue;
    row.dataset.hfDragReady = 'true';
    let down = false;
    let startX = 0;
    let startLeft = 0;

    row.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      down = true;
      startX = event.clientX;
      startLeft = row.scrollLeft;
      row.classList.add('is-dragging');
      row.setPointerCapture(event.pointerId);
    });
    row.addEventListener('pointermove', (event) => {
      if (!down) return;
      row.scrollLeft = startLeft - (event.clientX - startX);
    });
    const up = () => {
      down = false;
      row.classList.remove('is-dragging');
    };
    row.addEventListener('pointerup', up);
    row.addEventListener('pointercancel', up);
    row.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      row.scrollBy({
        left: (event.key === 'ArrowRight' ? 1 : -1) * row.clientWidth * 0.6,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
    });
  }
}

function scan(root = document) {
  scanReveals(root);
  setupDragRows(root);
}

if (!window.hfMotion) {
  window.hfMotion = true;
  scan();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) scan(event.target);
  });
  // Safety net: anything already on screen after load is shown even if the observer missed it.
  addEventListener('load', () => {
    setTimeout(() => {
      for (const el of document.querySelectorAll('[data-hf-reveal]:not(.is-in)')) {
        const box = el.getBoundingClientRect();
        if (box.top < innerHeight && box.bottom > 0) el.classList.add('is-in');
      }
    }, 2500);
  });
}
